import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Icon,
  Link,
  ListingImage,
  Switch,
  Text,
  theme,
  Title,
} from 'components';
import { useChannelStateContext, useChatContext } from 'stream-chat-react';
import { getOtherParticipants } from 'utils';
import { ContextualChatContext, RightSidePanelPageType } from 'contexts';
import useExportChat from 'hooks/contextualChat/useExportChat';
import { Loading } from '@app/react-common';
import { useBreakpoint } from 'hooks';

const SideDetailsPanel = () => {
  const { md } = useBreakpoint();
  const { channel } = useChannelStateContext();
  const {
    setSelectedContactId,
    setRightSidePanelPage,
    setChatModalVisible,
    setViewDetailsPanel,
  } = useContext(ContextualChatContext);
  const { client } = useChatContext();
  const { loading: exportLoading, startExport, cancelExport } = useExportChat();
  const { listingId, listingThumbnail, name: channelName } = channel.data || {};
  const [isChannelMuted, setIsChannelMuted] = useState(
    channel.muteStatus().muted,
  );
  const [isChannelArchived, setIsChannelArchived] = useState(
    channel.state.membership.archived_at ?? false,
  );
  const { userID: currentUserId } = client;

  const handleMuteChannel = async () => {
    if (isChannelMuted) {
      await channel.unmute();
      setIsChannelMuted(false);
      return;
    }
    await channel.mute();
    setIsChannelMuted(true);
  };

  const handleArchiveChannel = async () => {
    if (isChannelArchived) {
      await channel.unarchive();
      setIsChannelArchived(false);
      return;
    }
    await channel.archive();
    setIsChannelArchived(true);
  };

  const disableExportChat = useMemo(
    () => !channel?.state?.messages?.length,
    [channel?.state?.messages?.length],
  );

  return (
    <StyledDetailsContainer>
      <StyledPrimaryHeader>
        {!md && (
          <Icon
            type="antdArrowLeftOutlined"
            width={18}
            color="primary"
            onClick={() => {
              setViewDetailsPanel(false);
            }}
          />
        )}
        <Title level={3} style={{ margin: '25px 0px' }}>
          Details
        </Title>
      </StyledPrimaryHeader>
      {listingId && (
        <StyledDetailsListingCard style={{ marginBottom: '23px' }}>
          <ListingImage size={60} imageSrc={listingThumbnail} />
          <StyledListingDetailsContainer>
            <Title level={4} hideMargin>
              {channelName}
            </Title>
            {listingId && (
              <Link
                href={`#/listing/${listingId}/view`}
                onClick={() => setChatModalVisible(false)}
              >
                View details
              </Link>
            )}
          </StyledListingDetailsContainer>
        </StyledDetailsListingCard>
      )}
      <StyledButtonContainer>
        <StyledDetailsCard
          onClick={() =>
            setRightSidePanelPage(RightSidePanelPageType.SearchInChat)
          }
        >
          <Icon type="search" color="textPrimary" />
          <StyledText>Search chat</StyledText>
        </StyledDetailsCard>
        <StyledDetailsCard
          disabled={disableExportChat}
          onClick={() =>
            exportLoading ? cancelExport() : startExport(channel.cid)
          }
        >
          {exportLoading ? (
            <StyledLoadingContainer>
              <Loading noPadding />
              <Text style={{ minWidth: 'fit-content' }}>Cancel</Text>
            </StyledLoadingContainer>
          ) : (
            <>
              <Icon
                type="uploadOutline"
                color={disableExportChat ? 'disabledColor' : 'textPrimary'}
              />
              <StyledText disabled={disableExportChat}>Export chat</StyledText>
            </>
          )}
        </StyledDetailsCard>
      </StyledButtonContainer>
      <StyledDetailsCard
        onClick={() =>
          setRightSidePanelPage(RightSidePanelPageType.MediaLinkDocDetails)
        }
      >
        <StyledParticipantDetailsContainer>
          <Icon type="pictureOutlined" color="textPrimary" />
          <StyledText>Media, links, & docs</StyledText>
        </StyledParticipantDetailsContainer>
        <Icon
          type="antdRightOutlined"
          color="textPrimary"
          style={{ marginLeft: 'auto' }}
        />
      </StyledDetailsCard>
      {/* Conversation Users */}
      <StyledTitle level={3} hideMargin>
        In conversation with
      </StyledTitle>
      <StyledParticipantContainer>
        {getOtherParticipants(channel, currentUserId).map(member => (
          <StyledDetailsCard
            key={member.id}
            onClick={() => {
              // Navigates to the user card details
              setSelectedContactId(member.bbUserId);
              setRightSidePanelPage(RightSidePanelPageType.ContactDetails);
            }}
          >
            <StyledParticipantDetailsContainer>
              <Avatar
                user={{
                  ...member,
                  firstName: member.name.split(' ')[0] || '',
                  lastName: member.name.split(' ')[1] || '',
                }}
                width={43}
              />
              <StyledText
                ellipsis={143} // Truncates text to prevent overlap
              >
                {member.name}
              </StyledText>
            </StyledParticipantDetailsContainer>
            <Icon
              type="antdRightOutlined"
              color="textPrimary"
              style={{ marginLeft: 'auto' }}
            />
          </StyledDetailsCard>
        ))}
      </StyledParticipantContainer>
      {/* Channel Actions */}
      <StyledSection>
        <StyledMuteConversationContainer>
          <StyledMuteWrapper>
            <Icon
              type="bellOutlined"
              color="textPrimary"
              style={{ marginRight: '12px' }}
            />
            <Text>Mute this conversation</Text>
          </StyledMuteWrapper>
          <Switch checked={isChannelMuted} onClick={handleMuteChannel} />
        </StyledMuteConversationContainer>
        <StyledArchiveChatButton
          color={theme.getColor('red7')}
          strong
          onClick={handleArchiveChannel}
          style={{ cursor: 'pointer' }}
        >
          {isChannelArchived ? 'Unarchive' : 'Archive'} chat
        </StyledArchiveChatButton>
      </StyledSection>
    </StyledDetailsContainer>
  );
};

const StyledMuteWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px; // As per Veronika's 'Desktop-staging' section Figma
`;

const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 12px;
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  gap: 8px;
`;

const StyledText = styled(Text)`
  margin-left: 8px;
  align-self: center;
`;

const StyledTitle = styled(Title)`
  margin-top: 40px;
`;

const StyledDetailsContainer = styled.div`
  flex: 1;
  min-width: 300px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding: 0px 16px;
  border-left: 1px solid #e3e3e3;
  animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  // Todo: Animating width might be better than position. Revisit later
  @keyframes slideInRight {
    from {
      transform: translateX(5%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StyledDetailsListingCard = styled.div`
  background-color: #ffffff;
  display: flex;
  align-items: center;
  padding: 18px 12px;
  border-radius: 8px;
  gap: 12px;
  box-shadow: 0px 2px 8px 0px #00000026;
`;

const StyledListingDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const StyledDetailsCard = styled.button<{ disabled?: boolean }>`
  display: flex;
  max-height: fit-content;
  flex: 1;
  align-items: center;
  background-color: #ffffff;
  padding: 10px 12px;
  border-radius: 8px;
  box-shadow: ${({ disabled }) =>
    disabled ? 'none' : '0px 2px 8px 0px #00000026'};
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  &:hover {
    background-color: ${({ disabled }) => (disabled ? 'auto' : '#f5f5f5')};
  }
`;

const StyledParticipantContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 450px;
  padding: 8px; // Needs padding to be able to show shadow of inner cards
  background-color: transparent;
  overflow-y: auto;
`;

const StyledParticipantDetailsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMuteConversationContainer = styled.div`
  display: flex;
  gap: 12px;
  margin: 16px 0px;
  justify-content: space-between;
`;

const StyledArchiveChatButton = styled(Text)`
  cursor: pointer;
  margin-bottom: 16px;
`;

export default SideDetailsPanel;
